import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from '../utils/service';
import Spinner from './Spinner';
import styles from './CategoriesPage.module.css';
import './shared-content-styles.css'; // Direct import of shared styles
import AuthContext from "../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";

function CategoriesPage() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  // Progressive loading states
  const [categories, setCategories] = useState([]); // Changed from {} to []
  const [categoryLessons, setCategoryLessons] = useState({}); // Store lessons by category ID
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingLessons, setLoadingLessons] = useState({}); // Track loading state per category

  // Existing states
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [previousFile, setPreviousFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [column1Width, setColumn1Width] = useState('360px');
  const [readingProgress, setReadingProgress] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Split view states
  const [isSplitView, setIsSplitView] = useState(false);
  const [leftPaneFile, setLeftPaneFile] = useState(null);
  const [rightPaneFile, setRightPaneFile] = useState(null);
  const [leftPaneContent, setLeftPaneContent] = useState('');
  const [rightPaneContent, setRightPaneContent] = useState('');
  const [activePane, setActivePane] = useState('left'); // 'left' or 'right'
  const [loadingLeft, setLoadingLeft] = useState(false);
  const [loadingRight, setLoadingRight] = useState(false);

  // New split view enhancement states
  const [splitDividerPosition, setSplitDividerPosition] = useState(50); // Percentage
  const [isResizingSplit, setIsResizingSplit] = useState(false);
  const [leftPaneZoom, setLeftPaneZoom] = useState(1);
  const [rightPaneZoom, setRightPaneZoom] = useState(1);

  // Single view zoom state
  const [singleViewZoom, setSingleViewZoom] = useState(1);

  const dividerRef = useRef(null);
  const splitDividerRef = useRef(null);
  const leftPaneRef = useRef(null);
  const rightPaneRef = useRef(null);
  const singleViewRef = useRef(null);

  const [preventAutoExpand, setPreventAutoExpand] = useState(false);
  const [hasManuallyToggledCategory, setHasManuallyToggledCategory] = useState(false);
  const [initialLessonLoad, setInitialLessonLoad] = useState(true);

  const config = {
    headers: {
      Authorization: `Bearer ${user.token}`,
    },
  };

  // Cache configuration
  const CATEGORIES_CACHE_KEY = 'categories_page_categories';
  const LESSONS_CACHE_PREFIX = 'categories_page_lessons_';
  const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

  // Cache utility functions
  const getCachedData = (key) => {
    try {
      const cached = localStorage.getItem(key);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const now = new Date().getTime();

        if (now - timestamp < CACHE_DURATION) {
          return data;
        } else {
          localStorage.removeItem(key);
        }
      }
    } catch (error) {
      console.error('Error reading cache:', error);
      localStorage.removeItem(key);
    }
    return null;
  };

  const setCachedData = (key, data) => {
    try {
      const cacheObject = {
        data,
        timestamp: new Date().getTime()
      };
      localStorage.setItem(key, JSON.stringify(cacheObject));
    } catch (error) {
      console.error('Error setting cache:', error);
    }
  };

  const clearCache = () => {
    // Clear all related cache entries
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key === CATEGORIES_CACHE_KEY || key.startsWith(LESSONS_CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  };

  // New useEffect for split divider resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizingSplit) {
        const splitContainer = document.querySelector(`.${styles.splitViewContainer}`);
        if (splitContainer) {
          const containerRect = splitContainer.getBoundingClientRect();
          const mouseX = e.clientX - containerRect.left;
          const newPosition = Math.max(20, Math.min(80, (mouseX / containerRect.width) * 100));
          setSplitDividerPosition(newPosition);
        }
      }
    };

    const handleMouseUp = () => {
      if (isResizingSplit) {
        setIsResizingSplit(false);
      }
    };

    if (isResizingSplit) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingSplit]);

  // Function to calculate zoom for content
  const calculateZoom = (paneRef) => {
    if (!paneRef.current) return 1;

    const paneWidth = paneRef.current.offsetWidth;
    const contentWidth = 800; // Approximate content width
    const padding = 48; // Total horizontal padding
    const availableWidth = paneWidth - padding;

    // Calculate zoom to fit content width, with extended minimum and maximum limits
    const zoom = Math.max(0.25, Math.min(2.5, availableWidth / contentWidth));
    return zoom;
  };

  // Function to calculate true fit-to-width zoom (for fullscreen/reset functionality)
  const calculateFitToWidthZoom = (paneRef) => {
    if (!paneRef.current) return 1;

    const paneWidth = paneRef.current.offsetWidth;
    const contentWidth = 900; // Slightly larger estimate to be more conservative
    const padding = 64; // More generous padding
    const availableWidth = paneWidth - padding;

    // Calculate zoom to truly fit content width, with more conservative approach
    const baseZoom = Math.max(0.25, Math.min(2.5, availableWidth / contentWidth));

    // Apply a conservative reduction factor but NEVER go above 100%
    const adjustedZoom = Math.max(0.25, Math.min(1.0, baseZoom * 0.75));

    return adjustedZoom;
  };

  // Single view zoom functions
  const adjustSingleViewZoom = (direction) => {
    const increment = 0.1;
    const newZoom = direction === 'in'
      ? Math.min(2.5, singleViewZoom + increment)
      : Math.max(0.25, singleViewZoom - increment);
    setSingleViewZoom(newZoom);
  };

  const resetSingleViewZoom = () => {
    const zoom = calculateFitToWidthZoom(singleViewRef);
    setSingleViewZoom(zoom);
  };

  // UseEffect to handle automatic zoom calculation
  useEffect(() => {
    const updateZoom = () => {
      if (isSplitView) {
        const leftZoom = calculateFitToWidthZoom(leftPaneRef);
        const rightZoom = calculateFitToWidthZoom(rightPaneRef);
        setLeftPaneZoom(leftZoom);
        setRightPaneZoom(rightZoom);
      } else if (selectedFile && singleViewRef.current) {
        const zoom = calculateFitToWidthZoom(singleViewRef);
        setSingleViewZoom(zoom);
      }
    };

    if (isSplitView) {
      // Initial calculation for split view
      setTimeout(updateZoom, 100);

      // Update on window resize
      window.addEventListener('resize', updateZoom);

      return () => {
        window.removeEventListener('resize', updateZoom);
      };
    } else if (selectedFile) {
      // Initial calculation for single view
      setTimeout(updateZoom, 100);

      // Update on window resize
      window.addEventListener('resize', updateZoom);

      return () => {
        window.removeEventListener('resize', updateZoom);
      };
    }
  }, [isSplitView, splitDividerPosition, selectedFile]);

  // Split divider mouse down handler
  const handleSplitDividerMouseDown = (e) => {
    e.preventDefault();
    setIsResizingSplit(true);
  };

  // Zoom controls functions
  const adjustZoom = (pane, direction) => {
    const increment = 0.1;
    if (pane === 'left') {
      const newZoom = direction === 'in'
        ? Math.min(2.5, leftPaneZoom + increment)
        : Math.max(0.25, leftPaneZoom - increment);
      setLeftPaneZoom(newZoom);
    } else {
      const newZoom = direction === 'in'
        ? Math.min(2.5, rightPaneZoom + increment)
        : Math.max(0.25, rightPaneZoom - increment);
      setRightPaneZoom(newZoom);
    }
  };

  const resetZoom = (pane) => {
    if (pane === 'left') {
      const zoom = calculateFitToWidthZoom(leftPaneRef);
      setLeftPaneZoom(zoom);
    } else {
      const zoom = calculateFitToWidthZoom(rightPaneRef);
      setRightPaneZoom(zoom);
    }
  };

  // Content protection - disable printing and other actions
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Prevent Ctrl+P (Print)
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }

      // Prevent Ctrl+S (Save)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }

      // Prevent Ctrl+C (Copy) when viewing content
      if ((selectedFile || isSplitView) && (e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }

      // Prevent Ctrl+A (Select All) when viewing content
      if ((selectedFile || isSplitView) && (e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }

      // Prevent Ctrl+X (Cut) when viewing content
      if ((selectedFile || isSplitView) && (e.ctrlKey || e.metaKey) && e.key === 'x') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }

      // Prevent F12 (Developer Tools)
      if (e.key === 'F12') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }

      // Prevent Ctrl+Shift+I (Developer Tools)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }

      // Prevent Ctrl+Shift+J (Console)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'J') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }

      // Prevent Ctrl+U (View Source)
      if ((e.ctrlKey || e.metaKey) && e.key === 'u') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    const handleContextMenu = (e) => {
      if (selectedFile || isSplitView) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    // Disable print function
    const originalPrint = window.print;
    window.print = () => {
      console.log('Printing disabled');
    };

    // Add event listeners
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('contextmenu', handleContextMenu, true);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('contextmenu', handleContextMenu, true);
      window.print = originalPrint;
    };
  }, [selectedFile, isSplitView]);

  // Progressive loading: Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCategories(true);

      // Try cache first
      const cachedCategories = getCachedData(CATEGORIES_CACHE_KEY);

      if (cachedCategories) {
        const categoriesArray = Array.isArray(cachedCategories) ? cachedCategories : [];
        setCategories(categoriesArray);
        setLoadingCategories(false);

        // Background refresh
        try {
          const response = await axios.get('/lessons/categories-only?style=lessons', config);
          if (Array.isArray(response.data) && JSON.stringify(response.data) !== JSON.stringify(categoriesArray)) {
            setCategories(response.data);
            setCachedData(CATEGORIES_CACHE_KEY, response.data);
          }
        } catch (error) {
          console.error('Background categories fetch failed:', error);
        }
      } else {
        try {
          const response = await axios.get('/lessons/categories-only?style=lessons', config);
          console.log('Categories response:', response.data);
          const categoriesData = Array.isArray(response.data) ? response.data : [];
          setCategories(categoriesData);
          setCachedData(CATEGORIES_CACHE_KEY, categoriesData);
        } catch (error) {
          console.error('Error fetching categories:', error);
          setCategories([]);
        } finally {
          setLoadingCategories(false);
        }
      }
    };

    fetchCategories();
  }, []);

  // ——— NEW: redirect to first lesson if at /lessons ———
  useEffect(() => {
    if (!lessonId && !loadingCategories && categories.length > 0) {
      const firstCatId = categories[0]._id;
      axios.get(`/lessons/category/${firstCatId}`, config)
        .then(res => {
          const lessons = res.data;
          if (Array.isArray(lessons) && lessons.length > 0) {
            navigate(`/lessons/${lessons[0]._id}`, { replace: true });
          }
        })
        .catch(err => console.error('Error loading first category lessons:', err));
    }
  }, [lessonId, loadingCategories, categories, navigate]);

  // Load lessons for a specific category (separated from accordion logic)
  const loadCategoryLessons = async (categoryId, categoryTitle) => {
    // Check if already loaded
    if (categoryLessons[categoryId]) {
      return;
    }

    const cacheKey = `${LESSONS_CACHE_PREFIX}${categoryId}`;

    // Check cache first
    const cachedLessons = getCachedData(cacheKey);

    if (cachedLessons) {
      setCategoryLessons(prev => ({
        ...prev,
        [categoryId]: cachedLessons
      }));
      return;
    }

    // Set loading state
    setLoadingLessons(prev => ({ ...prev, [categoryId]: true }));

    try {
      const response = await axios.get(`/lessons/category/${categoryId}`, config);
      const lessons = response.data;

      setCategoryLessons(prev => ({
        ...prev,
        [categoryId]: lessons
      }));

      // Cache the lessons
      setCachedData(cacheKey, lessons);

    } catch (error) {
      console.error(`Error fetching lessons for category ${categoryTitle}:`, error);
    } finally {
      setLoadingLessons(prev => ({ ...prev, [categoryId]: false }));
    }
  };

  // Updated: Handle lesson selection when navigating directly to a lesson URL
  useEffect(() => {
    if (lessonId && categories.length > 0) {
      handleFileSelect(lessonId);
    }
  }, [lessonId, categories]);















useEffect(() => {
  const findAndExpandLessonCategory = async () => {
    // Only auto-expand if:
    // 1. We have a lessonId
    // 2. Categories are loaded
    // 3. User hasn't manually toggled any categories
    // 4. This is the initial lesson load OR no category is currently expanded
    if (lessonId && categories.length > 0 && !loadingCategories && 
        (!hasManuallyToggledCategory || (!expandedCategory && initialLessonLoad))) {
      
      // Check if lesson is already visible in any loaded category
      const isLessonVisible = Object.values(categoryLessons).flat().some(lesson => lesson._id === lessonId);
      
      if (isLessonVisible) {
        // Find which category contains this lesson
        for (const [categoryId, lessons] of Object.entries(categoryLessons)) {
          if (lessons.some(lesson => lesson._id === lessonId)) {
            const category = categories.find(cat => cat._id === categoryId);
            if (category && expandedCategory !== category.title) {
              setExpandedCategory(category.title);
              setInitialLessonLoad(false); // Mark initial load as complete
            }
            break;
          }
        }
        return;
      }

      // If lesson not visible, we need to find which category it belongs to
      for (const category of categories) {
        if (!categoryLessons[category._id]) {
          try {
            const response = await axios.get(`/lessons/category/${category._id}`, config);
            const lessons = response.data;
            
            setCategoryLessons(prev => ({
              ...prev,
              [category._id]: lessons
            }));

            const cacheKey = `${LESSONS_CACHE_PREFIX}${category._id}`;
            setCachedData(cacheKey, lessons);

            if (lessons.some(lesson => lesson._id === lessonId)) {
              setExpandedCategory(category.title);
              setInitialLessonLoad(false); // Mark initial load as complete
              break;
            }
          } catch (error) {
            console.error(`Error loading category ${category.title} to find lesson:`, error);
          }
        }
      }
    }
  };

  findAndExpandLessonCategory();
}, [lessonId, categories, categoryLessons, loadingCategories, hasManuallyToggledCategory, expandedCategory, initialLessonLoad]);










  // Reading progress tracker
  useEffect(() => {
    const handleScroll = () => {
      const contentElement = document.querySelector(`.${styles.content}`);
      if (contentElement) {
        const scrollTop = contentElement.scrollTop;
        const scrollHeight = contentElement.scrollHeight - contentElement.clientHeight;
        const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
        setReadingProgress(Math.min(100, Math.max(0, progress)));
      }
    };

    const contentElement = document.querySelector(`.${styles.content}`);
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
      return () => contentElement.removeEventListener('scroll', handleScroll);
    }
  }, [selectedFile]);

  // Updated adjustImagePaths function for CategoriesPage.js
  const adjustImagePaths = (content) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');

    // Remove checkbox containers and checkboxes more thoroughly
    doc.querySelectorAll('.list-bullet-todo-container').forEach(container => {
      container.remove();
    });

    doc.querySelectorAll('.list-bullet-todo').forEach(checkbox => {
      checkbox.remove();
    });

    // Remove any input checkboxes
    doc.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      checkbox.remove();
    });

    // Additional cleanup for any aria-label checkboxes
    doc.querySelectorAll('input[aria-label="Checkbox"]').forEach(checkbox => {
      checkbox.remove();
    });

    // Set list style for remaining list items
    doc.querySelectorAll('li').forEach(li => {
      li.style.listStyleType = 'disc';

      // Also remove any remaining checkbox elements within list items
      const checkboxContainers = li.querySelectorAll('.list-bullet-todo-container');
      checkboxContainers.forEach(container => container.remove());

      const checkboxes = li.querySelectorAll('input[type="checkbox"], .list-bullet-todo');
      checkboxes.forEach(checkbox => checkbox.remove());
    });

    return doc.body.innerHTML;
  };

  const loadLessonContent = async (file, pane = null) => {
    if (pane) {
      if (pane === 'left') setLoadingLeft(true);
      if (pane === 'right') setLoadingRight(true);
    } else {
      setLoading(true);
    }

    const encodedLessonName = encodeURIComponent(file.replace('.html', ''));
    if (!pane) {
      navigate(`/lessons/${encodedLessonName}`);
    }

    try {
      const response = await axios.get(`/lessons/${file}`, config);
      const adjustedContent = adjustImagePaths(response.data);

      if (pane === 'left') {
        setLeftPaneContent(adjustedContent);
        setLoadingLeft(false);
      } else if (pane === 'right') {
        setRightPaneContent(adjustedContent);
        setLoadingRight(false);
      } else {
        setFileContent(adjustedContent);
        setLoading(false);
        setReadingProgress(0);
      }
    } catch (error) {
      console.error('Error fetching file content:', error);
      if (pane === 'left') setLoadingLeft(false);
      if (pane === 'right') setLoadingRight(false);
      if (!pane) setLoading(false);
    }
  };

  const handleFileSelect = (file) => {
    if (isSplitView) {
      // In split view, load into the active pane
      if (activePane === 'left') {
        setPreviousFile(leftPaneFile);
        setLeftPaneFile(file);
        loadLessonContent(file, 'left');
      } else {
        setPreviousFile(rightPaneFile);
        setRightPaneFile(file);
        loadLessonContent(file, 'right');
      }
    } else {
      // Normal single view
      setPreviousFile(selectedFile);
      setSelectedFile(file);
      setSearchResults(null);
      setSearchQuery("");
      loadLessonContent(file);
    }
  };

  const toggleSplitView = () => {
    if (!isSplitView) {
      // Entering split view
      setIsSplitView(true);
      if (selectedFile) {
        setLeftPaneFile(selectedFile);
        setLeftPaneContent(fileContent);
        setActivePane('right'); // Set focus to right pane for next selection
      }
    } else {
      // Exiting split view
      setIsSplitView(false);
      setActivePane('left');
      // Keep the left pane content as the main content
      if (leftPaneFile) {
        setSelectedFile(leftPaneFile);
        setFileContent(leftPaneContent);
      }
      // Clear right pane
      setRightPaneFile(null);
      setRightPaneContent('');
    }
  };

  const swapPanes = () => {
    const tempFile = leftPaneFile;
    const tempContent = leftPaneContent;

    setLeftPaneFile(rightPaneFile);
    setLeftPaneContent(rightPaneContent);
    setRightPaneFile(tempFile);
    setRightPaneContent(tempContent);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setLoading(true);
    axios.get(`/lessons/search?query=${searchQuery}`, config)
      .then(response => {
        setSearchResults(response.data);
        setPreviousFile(selectedFile);
        setSelectedFile(null);
      })
      .catch(error => console.error('Error searching:', error))
      .finally(() => setLoading(false));
  };

  const handleGoBack = () => {
    if (previousFile) {
      handleFileSelect(previousFile);
    }
  };

  // FIXED: Completely separate accordion state from async loading
const toggleCategory = (categoryId, categoryTitle) => {
  console.log('toggleCategory called:', { categoryId, categoryTitle, currentExpanded: expandedCategory });
  
  // Mark that user has manually interacted with categories
  setHasManuallyToggledCategory(true);
  
  const isCurrentlyExpanded = expandedCategory === categoryTitle;
  
  if (isCurrentlyExpanded) {
    setExpandedCategory(null);
  } else {
    setExpandedCategory(categoryTitle);
    
    if (!categoryLessons[categoryId]) {
      loadCategoryLessons(categoryId, categoryTitle);
    }
  }
};

  const handleContextMenu = (e) => {
    e.preventDefault();
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    if (!isResizing) {
      setIsResizing(true);
    }
  };

  const handleMouseMove = (e) => {
    if (isResizing) {
      const container = document.getElementById('mainContent');
      if (container) {
        const containerOffsetLeft = container.offsetLeft;
        const containerWidth = container.offsetWidth;
        const mouseX = e.clientX - containerOffsetLeft;

        const newColumn1Width = Math.max(280, Math.min(600, mouseX));
        setColumn1Width(`${newColumn1Width}px`);
      }
    }
  };

  const handleMouseUp = (e) => {
    if (isResizing) {
      setIsResizing(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
    setColumn1Width(isSidebarCollapsed ? '360px' : '60px');
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  const formatLessonTitle = (title) => {
    return title || 'Untitled Lesson';
  };

  const getCategoryStats = (category) => {
    // Use the lessonCount from the category if available, otherwise fall back to loaded lessons
    const count = category.lessonCount || (categoryLessons[category._id] || []).length;
    return {
      total: count,
      completed: Math.floor(count * 0.3) // Mock completion data
    };
  };

  const getLessonTitle = (filename) => {
    // Find the lesson title from loaded category lessons
    const lesson = Object.values(categoryLessons).flat().find(lesson => lesson._id === filename);
    return lesson ? lesson.title : 'Lesson';
  };

  // Refresh cache function
  const refreshCache = async () => {
    clearCache();
    setCategories([]);
    setCategoryLessons({});
    setLoadingCategories(true);

    try {
      const response = await axios.get('/lessons/categories-only?style=lessons', config);
      const categoriesData = Array.isArray(response.data) ? response.data : [];
      setCategories(categoriesData);
      setCachedData(CATEGORIES_CACHE_KEY, categoriesData);
    } catch (error) {
      console.error('Error refreshing categories:', error);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  return (
    <div className={`${styles.categoriesPage} ${isFullscreen ? styles.fullscreen : ''}`}>
      {/* Progress Bar */}
      {(selectedFile || isSplitView) && !isSplitView && (
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${readingProgress}%` }}
          />
        </div>
      )}

      <div className={styles.mainContent} id="mainContent">
        {/* Enhanced Sidebar */}
        <div
          className={`${styles.sidebar} ${isSidebarCollapsed ? styles.collapsed : ''}`}
          style={{ width: column1Width }}
        >
          <div className={styles.sidebarHeader}>
            <div className={styles.headerTop}>
              <div className={styles.brandSection}>
                <div className={styles.brandIcon}>
                  <i className="fas fa-layer-group"></i>
                </div>
                <div className={styles.brandText}>
                  <h2 className={styles.sidebarTitle}>Categories</h2>
                  <span className={styles.categoryCount}>
                    {categories.length} categories
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                
                <button
                  className={styles.collapseButton}
                  onClick={toggleSidebar}
                  title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                  <i className={`fas fa-chevron-${isSidebarCollapsed ? 'right' : 'left'}`}></i>
                </button>
              </div>
            </div>

            {!isSidebarCollapsed && (
              <>
                <form onSubmit={handleSearch} className={styles.searchForm}>
                  <div className={styles.searchContainer}>
                    <i className="fas fa-search"></i>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search all lessons..."
                      className={styles.searchInput}
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={clearSearch}
                        className={styles.clearButton}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                  <button type="submit" className={styles.searchButton}>
                    <i className="fas fa-search"></i>
                    Search
                  </button>
                </form>

                {/* Split View Controls */}
                <div className={styles.splitViewControls}>
                  <button
                    onClick={toggleSplitView}
                    className={`${styles.splitViewButton} ${isSplitView ? styles.active : ''}`}
                    title={isSplitView ? 'Exit Split View' : 'Enable Split View'}
                  >
                    <i className={`fas ${isSplitView ? 'fa-compress' : 'fa-columns'}`}></i>
                    {isSplitView ? 'Single View' : 'Split View'}
                  </button>

                  {isSplitView && (
                    <>
                      <button
                        onClick={swapPanes}
                        className={styles.swapButton}
                        title="Swap Panes"
                      >
                        <i className="fas fa-exchange-alt"></i>
                        Swap
                      </button>

                      <div className={styles.activePaneSelector}>
                        <span className={styles.activePaneLabel}>Active pane:</span>
                        <button
                          onClick={() => setActivePane('left')}
                          className={`${styles.paneButton} ${activePane === 'left' ? styles.active : ''}`}
                        >
                          Left
                        </button>
                        <button
                          onClick={() => setActivePane('right')}
                          className={`${styles.paneButton} ${activePane === 'right' ? styles.active : ''}`}
                        >
                          Right
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {!isSidebarCollapsed && (
            <div className={styles.categoriesContainer}>
              {loadingCategories ? (
                <div className={styles.loadingContainer}>
                  <Spinner />
                  <p className={styles.loadingText}>Loading categories...</p>
                </div>
              ) : (
                Array.isArray(categories) && categories.map(category => {
                  return (
                    <div key={category._id} className={styles.categoryCard}>
                      <div
                        className={`${styles.categoryHeader} ${expandedCategory === category.title ? styles.expanded : ''}`}
                        onClick={() => toggleCategory(category._id, category.title)}
                      >
                        <div className={styles.categoryInfo}>
                          <div className={styles.categoryIcon}>
                            <i className="fas fa-folder"></i>
                          </div>
                          <div className={styles.categoryDetails}>
                            <h3 className={styles.categoryName}>{category.title}</h3>
                          </div>
                        </div>
                        <div className={styles.categoryActions}>
                          <span className={`${styles.arrow} fas fa-chevron-down`}></span>
                        </div>
                      </div>

                      {expandedCategory === category.title && (
                        <div className={styles.lessonsList}>
                          {loadingLessons[category._id] ? (
                            <div className={styles.loadingContainer}>
                              <div className={styles.spinner}></div>
                              <p>Loading lessons...</p>
                            </div>
                          ) : categoryLessons[category._id] && Array.isArray(categoryLessons[category._id]) ? (
                            categoryLessons[category._id].map(lesson => (
                              <div
                                key={lesson._id}
                                className={`${styles.lessonItem} ${
                                  (isSplitView && (leftPaneFile === lesson._id || rightPaneFile === lesson._id)) ||
                                  (!isSplitView && selectedFile === lesson._id)
                                    ? styles.selected : ''
                                }`}
                                onClick={() => handleFileSelect(lesson._id)}
                              >
                                <div className={styles.lessonIcon}>
                                  <i className="fas fa-file-alt"></i>
                                </div>
                                <div className={styles.lessonInfo}>
                                  <span className={styles.lessonTitle}>
                                    {formatLessonTitle(lesson.title)}
                                  </span>
                                </div>
                                {isSplitView && leftPaneFile === lesson._id && (
                                  <span className={styles.paneIndicator}>L</span>
                                )}
                                {isSplitView && rightPaneFile === lesson._id && (
                                  <span className={styles.paneIndicator}>R</span>
                                )}
                                {(!isSplitView && selectedFile === lesson._id) && (
                                  <div className={styles.activeIndicator}>
                                    ●
                                  </div>
                                )}
                              </div>
                            ))
                          ) : (
                            <p style={{ padding: '20px', textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                              No lessons found
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Resizable Divider */}
        <div
          ref={dividerRef}
          className={`${styles.resizeDivider} ${isResizing ? styles.resizing : ''}`}
          onMouseDown={handleMouseDown}
        >
          <div className={styles.dividerHandle}>
            <i className="fas fa-grip-lines-vertical"></i>
          </div>
        </div>

        {/* Content Area */}
        <div className={styles.contentWrapper}>
          {/* Content Header */}
          <div className={styles.contentHeader}>
            <div className={styles.headerLeft}>

              {!isSplitView && selectedFile && (
                <div className={styles.currentLesson}>
                  <h1 className={styles.lessonTitle}>
                    {getLessonTitle(selectedFile)}
                  </h1>

                </div>
              )}
            </div>
            <div className={styles.headerActions}>
              {/* Single View Zoom Controls */}
              {!isSplitView && selectedFile && (
                <div className={styles.singleViewZoomControls}>
                  <button
                    onClick={() => adjustSingleViewZoom('out')}
                    className={styles.zoomButton}
                    title="Zoom out"
                  >
                    <i className="fas fa-search-minus"></i>
                  </button>
                  <span className={styles.zoomLevel}>{Math.round(singleViewZoom * 100)}%</span>
                  <button
                    onClick={() => adjustSingleViewZoom('in')}
                    className={styles.zoomButton}
                    title="Zoom in"
                  >
                    <i className="fas fa-search-plus"></i>
                  </button>
                  <button
                    onClick={resetSingleViewZoom}
                    className={styles.zoomButton}
                    title="Fit to width"
                  >
                    <i className="fas fa-expand-arrows-alt"></i>
                  </button>
                </div>
              )}
              <button
                onClick={toggleFullscreen}
                className={styles.fullscreenButton}
                title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              >
                <i className={`fas ${isFullscreen ? 'fa-compress' : 'fa-expand'}`}></i>
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div
            className={styles.content}
            onContextMenu={handleContextMenu}
            style={{
              userSelect: (selectedFile || isSplitView) ? 'none' : 'auto',
              WebkitUserSelect: (selectedFile || isSplitView) ? 'none' : 'auto',
              MozUserSelect: (selectedFile || isSplitView) ? 'none' : 'auto',
              msUserSelect: (selectedFile || isSplitView) ? 'none' : 'auto'
            }}
          >
            {loading && !isSplitView ? (
              <div className={styles.loadingContainer}>
                <Spinner />
                <p className={styles.loadingText}>Loading lesson...</p>
              </div>
            ) : searchResults ? (
              <div className={styles.searchResultsContainer}>
                <div className={styles.searchHeader}>
                  <h2>
                    <i className="fas fa-search"></i>
                    Search Results for "{searchQuery}"
                  </h2>
                  <button onClick={clearSearch} className={styles.clearSearchButton}>
                    <i className="fas fa-times"></i>
                    Clear Search
                  </button>
                </div>
                {searchResults.length === 0 ? (
                  <div className={styles.noResults}>
                    <i className="fas fa-search"></i>
                    <h3>No results found</h3>
                    <p>Try adjusting your search terms or browse lessons from the categories.</p>
                  </div>
                ) : (
                  <div className={styles.searchResults}>
                    {searchResults.map((result, index) => (
                      <div key={result._id} className={styles.searchResultCard}>
                        <div className={styles.resultIcon}>
                          <i className="fas fa-file-alt"></i>
                        </div>
                        <div className={styles.resultContent}>
                          <h3 className={styles.resultTitle}>
                            {formatLessonTitle(result.title)}
                          </h3>
                          <p className={styles.resultDescription}>
                            Found matching content in this lesson
                          </p>
                        </div>
                        <button
                          onClick={() => handleFileSelect(result._id)}
                          className={styles.viewLessonButton}
                        >
                          <i className="fas fa-eye"></i>
                          View Lesson
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : isSplitView ? (
              <div className={styles.splitViewContainer}>
                {/* Left Pane */}
                <div
                  ref={leftPaneRef}
                  className={`${styles.splitPane} ${styles.leftPane} ${activePane === 'left' ? styles.activePane : ''}`}
                  style={{ width: `${splitDividerPosition}%` }}
                >
                  <div className={styles.splitPaneHeader}>
                    <h3>
                      {leftPaneFile ? getLessonTitle(leftPaneFile) : 'Select a lesson'}
                      {activePane === 'left' && <span className={styles.activeIndicator}>●</span>}
                    </h3>
                    <div className={styles.zoomControls}>
                      <button
                        onClick={() => adjustZoom('left', 'out')}
                        className={styles.zoomButton}
                        title="Zoom out"
                      >
                        <i className="fas fa-search-minus"></i>
                      </button>
                      <span className={styles.zoomLevel}>{Math.round(leftPaneZoom * 100)}%</span>
                      <button
                        onClick={() => adjustZoom('left', 'in')}
                        className={styles.zoomButton}
                        title="Zoom in"
                      >
                        <i className="fas fa-search-plus"></i>
                      </button>
                      <button
                        onClick={() => resetZoom('left')}
                        className={styles.zoomButton}
                        title="Fit to width"
                      >
                        <i className="fas fa-expand-arrows-alt"></i>
                      </button>
                    </div>
                  </div>
                  <div className={styles.splitPaneContent}>
                    {loadingLeft ? (
                      <Spinner />
                    ) : leftPaneContent ? (
                      <div
                        className={`${styles.lessonContent} lesson-content`}
                        style={{
                          transform: `scale(${leftPaneZoom})`,
                          transformOrigin: 'top left',
                          width: `${100 / leftPaneZoom}%`,
                          minHeight: `${100 / leftPaneZoom}%`,
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          MozUserSelect: 'none',
                          msUserSelect: 'none'
                        }}
                        dangerouslySetInnerHTML={{ __html: leftPaneContent }}
                      />
                    ) : (
                      <div className={styles.emptyPane}>
                        <i className="fas fa-file-alt"></i>
                        <p>Click a lesson to load it here</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Adjustable Divider */}
                <div
                  ref={splitDividerRef}
                  className={`${styles.splitDivider} ${isResizingSplit ? styles.resizing : ''}`}
                  onMouseDown={handleSplitDividerMouseDown}
                >
                  <div className={styles.splitDividerHandle}>
                    <i className="fas fa-grip-lines-vertical"></i>
                  </div>
                </div>

                {/* Right Pane */}
                <div
                  ref={rightPaneRef}
                  className={`${styles.splitPane} ${styles.rightPane} ${activePane === 'right' ? styles.activePane : ''}`}
                  style={{ width: `${100 - splitDividerPosition}%` }}
                >
                  <div className={styles.splitPaneHeader}>
                    <h3>
                      {rightPaneFile ? getLessonTitle(rightPaneFile) : 'Select a lesson'}
                      {activePane === 'right' && <span className={styles.activeIndicator}>●</span>}
                    </h3>
                    <div className={styles.zoomControls}>
                      <button
                        onClick={() => adjustZoom('right', 'out')}
                        className={styles.zoomButton}
                        title="Zoom out"
                      >
                        <i className="fas fa-search-minus"></i>
                      </button>
                      <span className={styles.zoomLevel}>{Math.round(rightPaneZoom * 100)}%</span>  
                      <button
                        onClick={() => adjustZoom('right', 'in')}
                        className={styles.zoomButton}
                        title="Zoom in"
                      >
                        <i className="fas fa-search-plus"></i>
                      </button>
                      <button
                        onClick={() => resetZoom('right')}
                        className={styles.zoomButton}
                        title="Fit to width"
                      >
                        <i className="fas fa-expand-arrows-alt"></i>
                      </button>
                    </div>
                  </div>
                  <div className={styles.splitPaneContent}>
                    {loadingRight ? (
                      <Spinner />
                    ) : rightPaneContent ? (
                      <div
                        className={`${styles.lessonContent} lesson-content`}
                        style={{
                          transform: `scale(${rightPaneZoom})`,
                          transformOrigin: 'top left',
                          width: `${100 / rightPaneZoom}%`,
                          minHeight: `${100 / rightPaneZoom}%`,
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          MozUserSelect: 'none',
                          msUserSelect: 'none'
                        }}
                        dangerouslySetInnerHTML={{ __html: rightPaneContent }}
                      />
                    ) : (
                      <div className={styles.emptyPane}>
                        <i className="fas fa-file-alt"></i>
                        <p>Click a lesson to load it here</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : selectedFile ? (
              <div className={styles.lessonContent} ref={singleViewRef}>
                <div
                  className={`${styles.readerContent} lesson-content`}
                  style={{
                    transform: `scale(${singleViewZoom})`,
                    transformOrigin: 'top left',
                    width: `${100 / singleViewZoom}%`,
                    minHeight: `${100 / singleViewZoom}%`,
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                    WebkitTouchCallout: 'none',
                    WebkitUserDrag: 'none',
                    KhtmlUserSelect: 'none'
                  }}
                  dangerouslySetInnerHTML={{ __html: fileContent }}
                />
              </div>
            ) : (
              <div className={styles.welcomeScreen}>
                <div className={styles.welcomeContent}>
                  <div className={styles.welcomeIcon}>
                    <i className="fas fa-layer-group"></i>
                  </div>
                  <h2>Explore Learning Categories</h2>
                  <p>Browse through organized categories to find the perfect lesson for your learning journey. Each category contains carefully curated content designed to build your knowledge progressively.</p>
                  <div className={styles.welcomeStats}>
                    <div className={styles.statItem}>
                      <i className="fas fa-layer-group"></i>
                      <span>{categories.length} Categories</span>
                    </div>
                    <div className={styles.statItem}>
                      <i className="fas fa-book"></i>
                      <span>{Object.values(categoryLessons).flat().length} Total Lessons</span>
                    </div>
                    <div className={styles.statItem}>
                      <i className="fas fa-search"></i>
                      <span>Searchable Content</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CategoriesPage;